import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Sun, Moon, LogOut } from 'lucide-react';
import { initialsOf } from './utils';

// Shared chrome pieces used across the three header concepts.

export const TagLogo = ({ size = 32, className = '' }) => (
  <img src="/tag-logo.png" alt="Logo" width={size} height={size} className={`shrink-0 object-contain ${className}`} style={{ width: size, height: size }} data-testid="tagws-logo" />
);

export const TagBrand = () => (
  <a href="/" className="flex min-w-0 items-center gap-2" data-testid="tagws-header-brand">
    <TagLogo size={34} />
    <span className="truncate text-sm font-semibold tracking-tight text-foreground sm:text-base">Tag Workspace</span>
  </a>
);

export const TagCompanyPill = ({ companyName }) => (
  companyName ? (
    <span className="hidden items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm md:flex" data-testid="tagws-header-company-pill">
      <Building2 size={14} className="text-muted-foreground" />
      <span className="max-w-[220px] truncate">{companyName}</span>
    </span>
  ) : (
    <span className="hidden items-center gap-2 rounded-full border border-dashed border-border bg-transparent px-3 py-1.5 text-sm text-muted-foreground md:flex" data-testid="tagws-header-company-pill">
      <Building2 size={14} /> Personal tags
    </span>
  )
);

export const TagThemeToggle = ({ isDark, onToggle }) => (
  <button
    type="button" onClick={onToggle} aria-label="Toggle light / dark theme" data-testid="tagws-header-theme-toggle"
    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-foreground active:scale-95"
  >
    {isDark ? <Sun size={17} /> : <Moon size={17} />}
  </button>
);

export const TagUserMenu = ({ principal, companyName, isTagAdmin, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const name = principal?.name || principal?.email || 'User';
  return (
    <div className="relative" ref={ref}>
      <button
        type="button" onClick={() => setOpen((o) => !o)} data-testid="tagws-header-user-menu"
        className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 text-sm text-foreground transition-colors duration-150 hover:bg-secondary active:scale-[0.98]"
      >
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[hsl(var(--accent))] text-xs font-bold text-foreground">{initialsOf(name)}</span>
        <span className="hidden max-w-[120px] truncate sm:block">{name}</span>
        <ChevronDown size={15} className="text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1.5 w-60 rounded-xl border border-border bg-popover p-1.5 shadow-lg" data-testid="tagws-user-menu-panel">
          <div className="px-2.5 py-2">
            <div className="truncate text-sm font-medium text-foreground">{name}</div>
            <div className="truncate text-xs text-muted-foreground">{principal?.email}</div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground"><Building2 size={11} /> {companyName || 'No company'}</span>
              {isTagAdmin && <span className="inline-flex items-center rounded-full border border-border bg-[hsl(var(--accent))] px-2 py-0.5 text-[length:calc(var(--fs,1)*0.6875rem)] font-medium text-foreground">Tag Admin</span>}
            </div>
          </div>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={onLogout} data-testid="tagws-logout-button"
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-secondary"
          >
            <LogOut size={15} className="text-muted-foreground" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};
